import {
  browserExtensionIgnoreList,
  errorMessageIgnoreList,
  errorSourceAllowList,
  errorSourceIgnoreList,
} from '@web-platform/shared-universal-constants/src/shouldLogErrorLists';
import { GLOBAL } from 'saddlebag-browser';

const $window: Window = GLOBAL.getWindow();

type ErrorFields = {
  message: string | undefined;
  sourceDomain: string | undefined;
  stack: string;
};

export const shouldLogError = ({ message, sourceDomain, stack }: ErrorFields) =>
  isInternalSourceError(sourceDomain) &&
  !isErrorMessageInIgnoreList(message) &&
  !isErrorFromBrowserExtensionIgnoreList(stack);

/*
  Check whether error message is in the ignore list
*/
export const isErrorMessageInIgnoreList = (message: string | undefined) =>
  errorMessageIgnoreList.some((ignoredErrorMessage) => {
    if (!message) {
      return false;
    }

    if (typeof ignoredErrorMessage === 'string') {
      return message.includes(ignoredErrorMessage);
    }

    if (ignoredErrorMessage instanceof RegExp) {
      return ignoredErrorMessage.test(message);
    }

    return false;
  });

/*
  If the source URL is hosted by Skyscanner.
*/
export const isInternalSourceError = (source: string | undefined) => {
  const isSourceAllow = errorSourceAllowList.some(
    (allowedEntry) => source && source.includes(allowedEntry),
  );

  const isSourceDeny = errorSourceIgnoreList.some(
    (denyEntry) => source && source.includes(denyEntry),
  );

  return isSourceAllow && !isSourceDeny;
};

/*
  The last frame of the stack trace _should_ be where the error originated from.
  However, some of the libraries we use end up intercepting unhandled errors,
  thus creating additional frames at the bottom.

  - New Relic sometimes adds a `nrWrapper` frame containing a reference to the
  page URL, which we want to ignore. This should have been fixed in v1222 of the
  New Relic browser agent, but still persists.A support case has been raised:
  https://support.newrelic.com/s/case/5008W00001BDsP5

  - PerimeterX may also be found in the stack trace. We are not fully aware of
  the reason yet and they insist that they do not have generic error handlers
  registered within their code.

  As a result, to find the source domain, we start looking at each frame from
  the bottom up. If a frame from New Relic or PerimeterX is encountered, we skip
  it and check the next one up. The first frame that is neither of the above
  should be the initiator. The first URL in that frame is the source domain.
*/
export const isErrorFromBrowserExtensionIgnoreList = (stack: string) =>
  browserExtensionIgnoreList.some((extensionId) => stack.includes(extensionId));

export const getSourceDomain = (stackTrace: string): string | undefined =>
  getSourceDomainRecursively(stackTrace.split('\n'));

const getSourceDomainRecursively = (stack: string[]): string | undefined => {
  if (stack.length === 0) {
    return undefined;
  }

  const lastFrame = stack[stack.length - 1];
  if (lastFrame.includes('nrWrapper') || lastFrame.includes('PerimeterX')) {
    return getSourceDomainRecursively(stack.slice(0, stack.length - 1));
  }

  return lastFrame.match(/https?:\/\/([A-Za-z0-9.-]+)/)?.[1];
};

/*
  Matches if an unhandled rejection is from a webpack chunk failing to load.

  This could be either `rejection.reason.name` or `rejection.reason.code` depending on which webpack plugin has thrown the error:
    - webpack uses `name`: https://github.com/webpack/webpack/blob/853bfda35a0080605c09e1bdeb0103bcb9367a10/lib/web/JsonpChunkLoadingRuntimeModule.js#L181
    - mini css extract uses `code`: https://github.com/webpack-contrib/mini-css-extract-plugin/blob/8b52feeac2e9d0b539feb1ccd08eaffdf01d50f5/src/index.js#L873
*/
export const isChunkLoadFailure = (name: string, code: string) =>
  name === 'ChunkLoadError' || code === 'CSS_CHUNK_LOAD_FAILED';

/*
 Returns resource timing information for a URL if available.
*/
export const getResourceTimingsForFailedUrl = (url?: string) => {
  // Checks that resourceTiming Api - https://www.w3.org/TR/resource-timing/ is supported
  const browserSupportsResourceTimingApi = !!$window.performance?.getEntriesByType;

  // Check to ensure that this wasn't called by accident to safeguard the error handler from throwing an error and that URL is not empty
  if (!browserSupportsResourceTimingApi || !url) return undefined;

  // Gets all resources
  const resourceList = $window.performance.getEntriesByType('resource');

  // filter all resources that match the given URL. This may return multiple performance entries which may want to be seen should this be a transient error. This caters for relative and full URLs by using includes() vs a direct string comparison
  return resourceList.filter((resource) => resource.name.includes(url));
};

/*
  We only do this for unactionable unhandled errors that we do wish to keep daily visibility of for safety, but do not wish to pollute our error or warning logs with. This util is only intended for this file.
*/
export const logUnhandled = (fields: {
  [field: string]: string;
  message: string;
  component: string;
  source: string;
}) => {
  if ($window.performance && $window.performance.mark) {
    $window.performance.mark('unhandled', { detail: fields });
  }
};
